import { z } from "zod"

/**
 * Цифровые артикулы для UGC-креаторов, арбитражников и продавца (ТЗ 31.10.2026).
 *
 * Артикул - 8 цифр, уникален на всю площадку, привязан к одному товару и
 * одному источнику (продавец, партнёр или площадка). Покупатель вводит его в
 * поиск, витрина или приложение открывает карточку товара и 30 дней помнит
 * артикул. Заказ товаров этого магазина засчитывается артикулу, партнёру
 * начисляется процент от выкупленных позиций магазина после скидок.
 *
 * Комиссия партнёра живёт в состояниях: hold (заказ оформлен, оплачен, в
 * пути), available (заказ завершён и прошло 14 дней на возврат), cancelled
 * (отмена, возврат, самопокупка), paid (выплачено площадкой).
 *
 * Ручки (префикс /api/v1):
 *
 * Покупатель (без входа или с входом):
 *   GET  /articles/resolve/:code            -> UgcResolveResult
 *   POST /articles/visit                    UgcVisitInput -> UgcVisitResult
 *   Оформление заказа принимает необязательное поле `ugcArticleCode` (последний
 *   артикул из памяти устройства). Без него шлюз ищет переход по артикулу за
 *   устройством/покупателем в окне 30 дней.
 *   Поиск каталога: запрос из 8 цифр, совпавший с активным артикулом, отдаёт
 *   в ответе поиска поле `ugcRedirect: UgcResolveResult` (старые клиенты его
 *   не читают и ничего не ломается).
 *
 * Продавец (кабинет, права магазина):
 *   GET    /seller/ugc/articles               ?productId&status&ownerKind&q -> UgcArticle[]
 *   POST   /seller/ugc/articles               UgcArticleInput -> UgcArticle
 *   PATCH  /seller/ugc/articles/:id           UgcArticlePatch -> UgcArticle
 *   DELETE /seller/ugc/articles/:id           -> { ok: true }
 *   GET    /seller/ugc/programs               -> UgcProductProgram[] (товары магазина с настройками)
 *   PUT    /seller/ugc/programs/:productId    UgcProgramInput -> UgcProductProgram
 *   GET    /seller/ugc/requests               ?status -> UgcAccessRequest[]
 *   POST   /seller/ugc/requests/:id/approve   { commissionPercent?: number } -> UgcAccessRequest
 *   POST   /seller/ugc/requests/:id/reject    { reason?: string } -> UgcAccessRequest
 *   POST   /seller/ugc/partners/:partnerId/block    { blocked: boolean } -> { ok: true }
 *   GET    /seller/ugc/analytics              UgcAnalyticsQuery -> UgcAnalytics
 *   GET    /seller/ugc/analytics/export       UgcAnalyticsQuery & { format: csv|xlsx|pdf } -> файл
 *
 * Партнёр (единый партнёрский кабинет, любой вошедший пользователь):
 *   GET   /partner/me                         -> UgcPartnerProfile | null
 *   POST  /partner/join                       UgcPartnerJoinInput -> UgcPartnerProfile
 *   PATCH /partner/me                         Partial<UgcPartnerJoinInput> -> UgcPartnerProfile
 *   GET   /partner/catalog                    UgcCatalogQuery -> { items: UgcCatalogItem[], total }
 *   GET   /partner/articles                   ?productId&status -> UgcArticle[]
 *   POST  /partner/articles                   UgcArticleInput -> UgcArticle
 *   POST  /partner/articles/bulk              UgcBulkInput -> UgcArticle[]
 *   PATCH /partner/articles/:id               UgcArticlePatch -> UgcArticle
 *   POST  /partner/access-requests            { productId, message? } -> UgcAccessRequest
 *   GET   /partner/stats                      UgcAnalyticsQuery -> UgcAnalytics (с balance)
 *   GET   /partner/commissions                ?state -> UgcCommission[]
 *
 * Консоль площадки:
 *   GET   /admin/ugc/articles                 ?q&shopId&partnerId&productId&status&ownerKind&page&pageSize -> { items, total }
 *   GET   /admin/ugc/articles/:id             -> UgcArticleDetail
 *   POST  /admin/ugc/articles                 UgcArticleInput -> UgcArticle (ownerKind platform)
 *   PATCH /admin/ugc/articles/:id             UgcArticlePatch & { status может быть blocked, reason } -> UgcArticle
 *   GET   /admin/ugc/summary                  ?from&to (days=0 или all=1 - всё время) -> UgcAnalytics
 *   GET   /admin/ugc/analytics                UgcAnalyticsQuery & { shopId?, all? } -> UgcAnalytics (с balance)
 *   GET   /admin/ugc/analytics/export         то же & { format: csv|xlsx|pdf } -> файл
 *   POST  /admin/ugc/commissions/:id/paid     -> UgcCommission (отметка о выплате партнёру)
 */

export const UGC_ARTICLE_LENGTH = 8
export const UGC_ATTRIBUTION_DAYS = 30
export const UGC_RETURN_WINDOW_DAYS = 14
export const UGC_MAX_ACTIVE_PER_PRODUCT_PARTNER = 50
export const UGC_BULK_MAX = 50

export const UgcArticleCodeSchema = z
  .string()
  .regex(/^[1-9][0-9]{7}$/, "Артикул должен состоять только из цифр и быть уникальным в системе")

/** Похоже ли на цифровой артикул - клиенты решают, звать ли resolve. */
export const looksLikeUgcArticle = (q: string): boolean => /^\s*[1-9][0-9]{7}\s*$/.test(q)

export const UgcOwnerKindSchema = z.enum(["seller", "partner", "platform"])
export type UgcOwnerKind = z.infer<typeof UgcOwnerKindSchema>

export const UgcPartnerRoleSchema = z.enum(["creator", "arbitrage"])
export type UgcPartnerRole = z.infer<typeof UgcPartnerRoleSchema>

/** Площадка для группировки в аналитике (VK vs Telegram vs Instagram). */
export const UgcChannelSchema = z.enum([
  "vk",
  "telegram",
  "instagram",
  "youtube",
  "tiktok",
  "dzen",
  "yandex_direct",
  "vk_ads",
  "offline",
  "other",
])
export type UgcChannel = z.infer<typeof UgcChannelSchema>

export const UgcArticleStatusSchema = z.enum(["moderation", "active", "disabled", "blocked", "deleted"])
export type UgcArticleStatus = z.infer<typeof UgcArticleStatusSchema>

export const UgcMetricsSchema = z.object({
  /** Переходы (ввод артикула в поиск, одно устройство - один переход в день). */
  clicks: z.number().int(),
  /** Оформленные заказы. */
  orders: z.number().int(),
  /** Оплаченные заказы. */
  paidOrders: z.number().int(),
  /** Выкупленные (завершённые) заказы. */
  completedOrders: z.number().int(),
  /** Возвраты и отмены после оплаты. */
  returns: z.number().int(),
  /** Конверсия переход -> оформленный заказ, %. */
  cr: z.number(),
  /** Сумма продаж (товары магазина после скидок, без доставки), руб. */
  gmv: z.number(),
  /** Начисленная комиссия партнёра (hold + available + paid), руб. */
  commission: z.number(),
})
export type UgcMetrics = z.infer<typeof UgcMetricsSchema>

export const UgcArticleSchema = z.object({
  id: z.string(),
  code: UgcArticleCodeSchema,
  productId: z.string(),
  productTitle: z.string(),
  productImage: z.string().nullable(),
  shopId: z.string(),
  shopName: z.string(),
  ownerKind: UgcOwnerKindSchema,
  partnerId: z.string().nullable(),
  partnerName: z.string().nullable(),
  partnerRole: UgcPartnerRoleSchema.nullable(),
  /** Название артикула: «ВКонтакте - Осенняя кампания». */
  name: z.string(),
  /** Назначение/площадка: «VK / Сообщество бренда». */
  placement: z.string(),
  campaign: z.string(),
  channel: UgcChannelSchema,
  status: UgcArticleStatusSchema,
  /** Процент партнёру на момент создания/одобрения; 0 у артикулов продавца и площадки. */
  commissionPercent: z.number(),
  statusReason: z.string().nullable(),
  metrics: UgcMetricsSchema,
  /** Только в GET /seller/ugc/articles: партнёр артикула заблокирован этим магазином. */
  partnerBlocked: z.boolean().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type UgcArticle = z.infer<typeof UgcArticleSchema>

export const UgcArticleInputSchema = z.object({
  productId: z.string().min(1),
  name: z.string().trim().min(1).max(120),
  placement: z.string().trim().max(160).default(""),
  campaign: z.string().trim().max(160).default(""),
  channel: UgcChannelSchema.default("other"),
})
export type UgcArticleInput = z.infer<typeof UgcArticleInputSchema>

export const UgcArticlePatchSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  placement: z.string().trim().max(160).optional(),
  campaign: z.string().trim().max(160).optional(),
  channel: UgcChannelSchema.optional(),
  /** Продавец и партнёр: active | disabled. Консоль: ещё blocked. */
  status: UgcArticleStatusSchema.optional(),
  reason: z.string().trim().max(500).optional(),
})
export type UgcArticlePatch = z.infer<typeof UgcArticlePatchSchema>

export const UgcBulkInputSchema = z.object({
  productId: z.string().min(1),
  /** Источники: «Yandex_Direct_Camp_1», «Telegram_Ad_Net_2»… */
  sources: z
    .array(
      z.object({
        name: z.string().trim().min(1).max(120),
        placement: z.string().trim().max(160).optional(),
        campaign: z.string().trim().max(160).optional(),
        channel: UgcChannelSchema.optional(),
      }),
    )
    .min(1)
    .max(UGC_BULK_MAX),
})
export type UgcBulkInput = z.infer<typeof UgcBulkInputSchema>

export const UgcResolveResultSchema = z.discriminatedUnion("found", [
  z.object({
    found: z.literal(true),
    code: UgcArticleCodeSchema,
    articleId: z.string(),
    productId: z.string(),
    /** Путь карточки на витрине: /product/<id>. Приложение открывает свой экран товара по productId. */
    productPath: z.string(),
    shopId: z.string(),
    partnerId: z.string().nullable(),
  }),
  z.object({
    found: z.literal(false),
    code: z.string(),
    /** not_found - нет такого; inactive - отключён, заблокирован или удалён. */
    reason: z.enum(["not_found", "inactive", "invalid"]),
    message: z.string(),
  }),
])
export type UgcResolveResult = z.infer<typeof UgcResolveResultSchema>

export const UgcVisitInputSchema = z.object({
  code: UgcArticleCodeSchema,
  /** Идентификатор устройства (тот же, что у /referral/landing и событий витрины). */
  clientId: z.string().min(1).max(128),
  surface: z.enum(["web", "ios", "android"]).default("web"),
})
export type UgcVisitInput = z.infer<typeof UgcVisitInputSchema>

export const UgcVisitResultSchema = z.object({
  ok: z.boolean(),
  code: z.string(),
  articleId: z.string().nullable(),
  productId: z.string().nullable(),
  /** До какого момента артикул засчитывается заказам с этого устройства. */
  expiresAt: z.string().nullable(),
})
export type UgcVisitResult = z.infer<typeof UgcVisitResultSchema>

/** Что клиент хранит у себя (localStorage / AsyncStorage) 30 дней. */
export const UgcStoredAttributionSchema = z.object({
  code: UgcArticleCodeSchema,
  articleId: z.string(),
  productId: z.string(),
  shopId: z.string(),
  partnerId: z.string().nullable(),
  savedAt: z.string(),
  expiresAt: z.string(),
})
export type UgcStoredAttribution = z.infer<typeof UgcStoredAttributionSchema>

export const UgcProgramInputSchema = z.object({
  /** «Доступно всем»: партнёры генерируют артикулы без заявки. */
  openToAll: z.boolean(),
  /** Комиссия партнёра, % от выкупленной позиции после скидок магазина. */
  commissionPercent: z.number().min(0).max(50),
})
export type UgcProgramInput = z.infer<typeof UgcProgramInputSchema>

export const UgcProductProgramSchema = z.object({
  productId: z.string(),
  productTitle: z.string(),
  productImage: z.string().nullable(),
  price: z.number(),
  openToAll: z.boolean(),
  commissionPercent: z.number(),
  articles: z.number().int(),
  partners: z.number().int(),
  pendingRequests: z.number().int(),
  metrics: UgcMetricsSchema,
})
export type UgcProductProgram = z.infer<typeof UgcProductProgramSchema>

export const UgcAccessRequestStatusSchema = z.enum(["pending", "approved", "rejected"])
export const UgcAccessRequestSchema = z.object({
  id: z.string(),
  productId: z.string(),
  productTitle: z.string(),
  shopId: z.string(),
  shopName: z.string(),
  partnerId: z.string(),
  partnerName: z.string(),
  partnerRole: UgcPartnerRoleSchema,
  message: z.string(),
  status: UgcAccessRequestStatusSchema,
  /** Персональная комиссия, если продавец задал при одобрении. */
  commissionPercent: z.number().nullable(),
  reason: z.string().nullable(),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
})
export type UgcAccessRequest = z.infer<typeof UgcAccessRequestSchema>

export const UgcPartnerJoinInputSchema = z.object({
  role: UgcPartnerRoleSchema,
  displayName: z.string().trim().min(1).max(120),
  /** Ссылки на каналы/аккаунты, свободным текстом. */
  channels: z.array(z.string().trim().max(300)).max(20).default([]),
})
export type UgcPartnerJoinInput = z.infer<typeof UgcPartnerJoinInputSchema>

export const UgcPartnerProfileSchema = z.object({
  partnerId: z.string(),
  userId: z.string(),
  role: UgcPartnerRoleSchema,
  displayName: z.string(),
  channels: z.array(z.string()),
  active: z.boolean(),
  createdAt: z.string(),
})
export type UgcPartnerProfile = z.infer<typeof UgcPartnerProfileSchema>

export const UgcCatalogQuerySchema = z.object({
  q: z.string().optional(),
  categoryId: z.string().optional(),
  minCommission: z.coerce.number().optional(),
  /** commission - по проценту; cr - по конверсии артикулов; new - новые. */
  sort: z.enum(["commission", "cr", "new"]).default("commission"),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(24),
})
export type UgcCatalogQuery = z.infer<typeof UgcCatalogQuerySchema>

export const UgcCatalogItemSchema = z.object({
  productId: z.string(),
  productTitle: z.string(),
  productImage: z.string().nullable(),
  price: z.number(),
  shopId: z.string(),
  shopName: z.string(),
  categoryName: z.string().nullable(),
  commissionPercent: z.number(),
  /** open - «Доступно всем»; approved - заявка одобрена; requestable - можно запросить; pending/rejected - заявка в работе/отклонена; blocked - продавец закрыл доступ. */
  access: z.enum(["open", "approved", "requestable", "pending", "rejected", "blocked"]),
  /** Конверсия всех артикулов товара, %, для арбитражников. */
  cr: z.number(),
  myArticles: z.number().int(),
})
export type UgcCatalogItem = z.infer<typeof UgcCatalogItemSchema>

export const UgcCommissionStateSchema = z.enum(["hold", "available", "cancelled", "paid"])
export type UgcCommissionState = z.infer<typeof UgcCommissionStateSchema>

export const UgcCommissionSchema = z.object({
  id: z.string(),
  articleId: z.string(),
  code: z.string(),
  orderId: z.string(),
  orderNumber: z.string().nullable(),
  partnerId: z.string(),
  shopId: z.string(),
  /** База: товары магазина в заказе после скидок, без доставки. */
  base: z.number(),
  percent: z.number(),
  amount: z.number(),
  state: UgcCommissionStateSchema,
  /** cancelled: order_cancelled | returned | self_purchase | blocked. */
  stateReason: z.string().nullable(),
  /** Когда hold станет available (завершение заказа + 14 дней). */
  availableAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type UgcCommission = z.infer<typeof UgcCommissionSchema>

export const UgcAnalyticsQuerySchema = z.object({
  from: z.string().optional(),
  to: z.string().optional(),
  /** 7 | 30 | 90 дней, если нет from/to; 0 - всё время (с первого артикула). */
  days: z.coerce.number().int().optional(),
  groupBy: z.enum(["article", "partner", "channel", "product", "campaign"]).default("article"),
  productId: z.string().optional(),
  partnerId: z.string().optional(),
})
export type UgcAnalyticsQuery = z.infer<typeof UgcAnalyticsQuerySchema>

export const UgcAnalyticsRowSchema = z.object({
  key: z.string(),
  label: z.string(),
  /** Для groupBy=article: код артикула, товар, партнёр, статус. */
  code: z.string().nullable(),
  productTitle: z.string().nullable(),
  partnerName: z.string().nullable(),
  channel: UgcChannelSchema.nullable(),
  status: UgcArticleStatusSchema.nullable(),
  metrics: UgcMetricsSchema,
})
export type UgcAnalyticsRow = z.infer<typeof UgcAnalyticsRowSchema>

export const UgcBalanceSchema = z.object({
  hold: z.number(),
  available: z.number(),
  paid: z.number(),
  cancelled: z.number(),
})
export type UgcBalance = z.infer<typeof UgcBalanceSchema>

export const UgcAnalyticsSchema = z.object({
  from: z.string(),
  to: z.string(),
  groupBy: UgcAnalyticsQuerySchema.shape.groupBy,
  totals: UgcMetricsSchema,
  rows: z.array(UgcAnalyticsRowSchema),
  /** По дням для графиков: переходы, заказы, выкуп, комиссия. */
  series: z.array(
    z.object({
      day: z.string(),
      clicks: z.number().int(),
      orders: z.number().int(),
      completedOrders: z.number().int(),
      commission: z.number(),
    }),
  ),
  /** Только у партнёра и в консоли. */
  balance: UgcBalanceSchema.nullable(),
})
export type UgcAnalytics = z.infer<typeof UgcAnalyticsSchema>

export const UgcArticleEventSchema = z.object({
  at: z.string(),
  kind: z.enum(["created", "status", "visit", "order", "commission", "edit"]),
  text: z.string(),
  actor: z.string().nullable(),
})
export type UgcArticleEvent = z.infer<typeof UgcArticleEventSchema>

export const UgcArticleDetailSchema = UgcArticleSchema.extend({
  orders: z.array(
    z.object({
      orderId: z.string(),
      orderNumber: z.string().nullable(),
      status: z.string(),
      base: z.number(),
      commission: z.number(),
      commissionState: UgcCommissionStateSchema.nullable(),
      /** Начисление партнёру по заказу (для POST /admin/ugc/commissions/:id/paid); null - артикул продавца/площадки. */
      commissionId: z.string().nullable(),
      createdAt: z.string(),
    }),
  ),
  events: z.array(UgcArticleEventSchema),
})
export type UgcArticleDetail = z.infer<typeof UgcArticleDetailSchema>

export const UGC_TEXTS = {
  empty: "У вас пока нет созданных артикулов. Нажмите «Создать артикул», чтобы начать привлекать трафик",
  noAccess: "Продавец ограничил возможность генерации артикулов для этого товара. Отправьте запрос продавцу",
  notFound: "Введённый артикул недействителен или был отключён владельцем",
  noStats: "По данному артикулу пока нет переходов и продаж. Данные обновляются в реальном времени",
  invalid: "Артикул должен состоять только из цифр и быть уникальным в системе",
} as const
